import { openDB } from 'idb';

const DB_NAME = 'kiranaflow_offline';
const DB_VERSION = 1;

let _dbPromise = null;

function getDB() {
  if (_dbPromise) return _dbPromise;
  try {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('products')) {
          db.createObjectStore('products', { keyPath: 'catalog_id' });
        }
        if (!db.objectStoreNames.contains('customers')) {
          db.createObjectStore('customers', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('offline_queue')) {
          const store = db.createObjectStore('offline_queue', {
            keyPath: 'local_id',
            autoIncrement: true,
          });
          store.createIndex('by_status', 'status');
        }
        if (!db.objectStoreNames.contains('sales_cache')) {
          db.createObjectStore('sales_cache', { keyPath: 'bill_number' });
        }
      },
    });
    return _dbPromise;
  } catch {
    // IndexedDB unavailable (e.g. Safari private mode) — return no-op stub
    const noopArr = async () => [];
    const stub = {
      getAll: noopArr,
      getAllFromIndex: noopArr,
      put: async () => {},
      add: async () => null,
      get: async () => null,
      clear: async () => {},
      transaction: () => ({
        store: { put: async () => {}, get: async () => null },
        done: Promise.resolve(),
      }),
    };
    _dbPromise = Promise.resolve(stub);
    return _dbPromise;
  }
}

// ── Products ──────────────────────────────────────────────────────────────────

export async function putProducts(items) {
  if (!items.length) return;
  const db = await getDB();
  const now = Date.now();
  const tx = db.transaction('products', 'readwrite');
  await Promise.all([
    ...items.map(item => tx.store.put({ ...item, _synced_at: now })),
    tx.done,
  ]);
}

// Full replacement — clears stale/deleted entries before writing fresh data.
// Use this for full API refreshes only. putProducts() stays as an upsert
// for incremental single-item writes (e.g. Quick Add Product).
export async function replaceAllProducts(items) {
  if (!items.length) return;
  const db = await getDB();
  const now = Date.now();
  const tx = db.transaction('products', 'readwrite');
  tx.store.clear();
  await Promise.all([
    ...items.map(item => tx.store.put({ ...item, _synced_at: now })),
    tx.done,
  ]);
}

export async function getAllProducts() {
  const db = await getDB();
  return db.getAll('products');
}

export async function removeProduct(catalogId) {
  if (!catalogId) return;
  const db = await getDB();
  await db.delete('products', catalogId);
}

// ── Customers ─────────────────────────────────────────────────────────────────

export async function putCustomers(items) {
  if (!items.length) return;
  const db = await getDB();
  const now = Date.now();
  const tx = db.transaction('customers', 'readwrite');
  await Promise.all([
    ...items.map(item => tx.store.put({ ...item, _synced_at: now })),
    tx.done,
  ]);
}

export async function getAllCustomers() {
  const db = await getDB();
  return db.getAll('customers');
}

// ── Offline queue ─────────────────────────────────────────────────────────────

export async function enqueueOfflineSale(entry) {
  const db = await getDB();
  return db.add('offline_queue', entry);
}

export async function getPendingQueueItems() {
  const db = await getDB();
  return db.getAllFromIndex('offline_queue', 'by_status', 'pending');
}

export async function getAllQueueItems() {
  const db = await getDB();
  return db.getAll('offline_queue');
}

export async function updateQueueItem(local_id, patch) {
  const db = await getDB();
  const tx = db.transaction('offline_queue', 'readwrite');
  const existing = await tx.store.get(local_id);
  if (existing) {
    await tx.store.put({ ...existing, ...patch });
  }
  await tx.done;
}

// ── Sales cache ───────────────────────────────────────────────────────────────

export async function putSalesCache(sales) {
  if (!sales.length) return;
  const db = await getDB();
  const now = Date.now();
  const tx = db.transaction('sales_cache', 'readwrite');
  await Promise.all([
    ...sales.map(s => tx.store.put({ ...s, _cached_at: now })),
    tx.done,
  ]);
}

export async function getSalesByDateRange(startDate, endDate) {
  const db = await getDB();
  const all = await db.getAll('sales_cache');
  if (!startDate && !endDate) return all;
  const start = startDate ? new Date(startDate + 'T00:00:00').getTime() : 0;
  const end = endDate ? new Date(endDate + 'T23:59:59').getTime() : Infinity;
  return all.filter(s => {
    const t = new Date(s.created_at).getTime();
    return t >= start && t <= end;
  });
}

// ── Products store-id stamp ───────────────────────────────────────────────────
// Persists the store_id that was active when products were last written to
// IndexedDB. Checked on POS load to detect cross-store contamination (e.g.
// admin switching between impersonation sessions).

const PRODUCTS_STORE_ID_KEY = 'kf_products_store_id';

export function getProductsStoreId() {
  try { return localStorage.getItem(PRODUCTS_STORE_ID_KEY) || null; }
  catch { return null; }
}

export function setProductsStoreId(storeId) {
  if (!storeId) return;
  try { localStorage.setItem(PRODUCTS_STORE_ID_KEY, storeId); }
  catch {}
}

// ── Temp bill number ──────────────────────────────────────────────────────────

export function generateTempBillNumber() {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const rand = Math.random().toString(16).slice(2, 6).toUpperCase();
  return `TMP-${date}-${rand}`;
}
